import React, { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Divider from '@material-ui/core/Divider';
import { FormattedMessage } from 'react-intl';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import TutorialToc from './TutorialToc';
import useTutorialOffsets from './useTutorialOffsets';
import GroupManagement from './Sections/GroupManagement';
import LessonManagement from './Sections/LessonManagement';
import AiAssistant from './Sections/AiAssistant';
import IndividualBoardManagement from './Sections/IndividualBoardManagement';
import TutorialLibrary from './Sections/TutorialLibrary';
import MobileManagement from './Sections/MobileManagement';
import GuestAccess from './Sections/GuestAccess';
import { sections, LEGACY_SECTION_IDS } from './constants';
import { parseQuery } from '../../common/utils';
import { COLORS, REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

// The contents move from a sidebar to a bar under the nav at the same point
// the nav itself collapses.
const WIDE_QUERY = '(min-width: 1080px)';
const WIDE = `@media ${WIDE_QUERY}`;

const SHOT_SHADOW = '0 1px 2px rgba(6, 63, 69, 0.06), 0 12px 32px rgba(6, 63, 69, 0.09)';

const useStyles = makeStyles(() => ({
    intro: {
        maxWidth: '900px',
        margin: '0 auto',
        textAlign: 'center',
    },
    eyebrowRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '22px',
    },
    title: {
        margin: '0 0 18px',
        fontSize: 'clamp(2rem, 4.4vw, 3.4rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
    },
    lead: {
        maxWidth: '680px',
        margin: '0 auto',
        color: REDESIGN_COLORS.body,
        fontSize: '1.06rem',
        lineHeight: 1.6,
        '& b': {
            color: REDESIGN_COLORS.dark,
            fontWeight: REDESIGN_FONTS.weightHeading,
        },
    },
    layout: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        alignItems: 'start',
        gap: '24px',
        [WIDE]: {
            gridTemplateColumns: 'minmax(0, 1fr) 280px',
            gap: '32px',
        },
    },
    // The section components render Material-UI Typography on the old theme.
    // Their markup and copy stay as they are; the redesign's type and frames
    // are applied from here, scoped to this card.
    content: {
        boxSizing: 'border-box',
        minWidth: 0,
        padding: 'clamp(20px, 3vw, 40px)',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '24px',
        background: REDESIGN_COLORS.white,
        fontFamily: REDESIGN_FONTS.body,
        color: REDESIGN_COLORS.body,
        '& .MuiTypography-root': {
            fontFamily: REDESIGN_FONTS.body,
        },
        '& .MuiTypography-h1, & .MuiTypography-h2': {
            margin: 0,
            padding: '8px 0 10px',
            fontSize: 'clamp(1.6rem, 2.6vw, 2.15rem)',
            lineHeight: 1.12,
            letterSpacing: '-0.03em',
            fontWeight: REDESIGN_FONTS.weightHeading,
            color: REDESIGN_COLORS.dark,
        },
        '& .MuiTypography-h3': {
            margin: 0,
            padding: '26px 0 8px',
            fontSize: 'clamp(1.25rem, 2vw, 1.55rem)',
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            fontWeight: REDESIGN_FONTS.weightHeading,
            color: REDESIGN_COLORS.dark,
        },
        '& .MuiTypography-h4': {
            margin: 0,
            padding: '0 0 14px',
            fontSize: '1.04rem',
            lineHeight: 1.6,
            fontWeight: 500,
            color: REDESIGN_COLORS.body,
        },
        '& .MuiTypography-h5, & .MuiTypography-h6': {
            fontSize: '0.98rem',
            lineHeight: 1.55,
            fontWeight: 600,
            color: REDESIGN_COLORS.body,
        },
        '& .MuiTypography-body1, & .MuiTypography-body2, & .MuiTypography-subtitle1': {
            fontSize: '0.95rem',
            lineHeight: 1.6,
            color: REDESIGN_COLORS.body,
        },
        '& b, & strong': {
            padding: 0,
            fontWeight: REDESIGN_FONTS.weightHeading,
            color: REDESIGN_COLORS.dark,
        },
        '& ul': {
            margin: '6px 0 10px',
            paddingLeft: '22px',
        },
        '& li': {
            margin: '4px 0',
        },
        '& img': {
            display: 'block',
            width: '100%',
            height: 'auto',
            border: `1px solid ${REDESIGN_COLORS.line}`,
            borderRadius: '14px',
            boxShadow: SHOT_SHADOW,
            background: REDESIGN_COLORS.white,
        },
        '& iframe': {
            border: 0,
            borderRadius: '16px',
            boxShadow: SHOT_SHADOW,
        },
        '& hr': {
            width: '100%',
            height: 0,
            margin: '32px 0 40px',
            border: 0,
            borderTop: `1px solid ${REDESIGN_COLORS.sand}`,
        },
        // Sections are jump targets; keep them clear of the pinned contents.
        '& section, & .MuiGrid-container': {
            scrollMarginTop: ({ scrollOffset }) => `${scrollOffset}px`,
        },
    },
    // Inline tool icons the lesson sections drop into their sentences.
    sampleFab: {
        '& span': {
            padding: 0,
        },
        '& svg': {
            padding: 0,
            verticalAlign: 'middle',
        },
        verticalAlign: 'middle',
        width: '36px',
        height: '36px',
        color: COLORS.primary,
        padding: 0,
        marginLeft: '1rem',
    },
}));

function getSectionUrl(location) {
    const queryObj = parseQuery(location.search);
    return queryObj.feature || null;
}

/**
 * The tutorial: a centred header, then the chapters in a white card with the
 * contents beside them (or in a sticky bar on narrow screens). Reached both
 * signed out and from the app drawer, and deep-linked from the features page
 * and the whiteboard with `?feature=<section>`.
 */
const Tutorial = ({ language }) => {
    const location = useLocation();
    const history = useHistory();
    const wide = useMediaQuery(WIDE_QUERY);
    const { scrollOffset } = useTutorialOffsets(wide);
    const classes = useStyles({ scrollOffset });
    // The section components register their DOM nodes here by lowercase id.
    const refs = useRef([]);

    useEffect(() => {
        const fromMenu = history.location.state && history.location.state.sectionId;
        const requested = fromMenu || `section-${getSectionUrl(location)}`;
        if (!requested) return undefined;
        // Sections that moved keep their old deep links.
        const sectionId = LEGACY_SECTION_IDS[requested.toLowerCase()] || requested;
        // A menu click can scroll right away, and glides. A deep link waits for
        // the first images to load so the target has settled, then jumps: it
        // should land on the section, not animate to it.
        const timer = setTimeout(
            () => {
                const el = refs.current[sectionId.toLowerCase()];
                if (el) {
                    window.scrollTo({
                        top: el.getBoundingClientRect().top + window.scrollY - scrollOffset,
                        behavior: fromMenu ? 'smooth' : 'auto',
                    });
                }
            },
            fromMenu ? 100 : 800
        );
        return () => clearTimeout(timer);
    }, [history.location.state, location, scrollOffset]);

    const sectionProps = { language, classes, refs };

    return (
        <>
            <LandingSection id="top" maxWidth={1080} paddingTop={46} paddingBottom={16}>
                <div className={classes.intro}>
                    <div className={classes.eyebrowRow}>
                        <SectionEyebrow>
                            <FormattedMessage id="tutorial" />
                        </SectionEyebrow>
                    </div>
                    <h1 className={classes.title}>
                        <FormattedMessage id="redesign.page.tutorial.title1" />
                    </h1>
                    <p className={classes.lead}>
                        <FormattedMessage
                            id="redesign.page.tutorial.title2"
                            values={{ b: (chunks) => <b>{chunks}</b> }}
                        />
                    </p>
                </div>
            </LandingSection>

            {/* The contents are sticky, so this section must not clip. */}
            <LandingSection paddingTop={16} paddingBottom={64} overflow="visible">
                <div
                    id="section-tutorialStart"
                    ref={(el) => {
                        if (el && el.id) {
                            refs.current[el.id.toLowerCase()] = el;
                        }
                    }}
                    className={classes.layout}
                >
                    {wide ? null : <TutorialToc sections={sections} variant="bar" />}

                    <div className={classes.content}>
                        <GroupManagement {...sectionProps} />
                        <Divider variant="middle" />
                        <LessonManagement {...sectionProps} />
                        <Divider variant="middle" />
                        <AiAssistant {...sectionProps} />
                        <Divider variant="middle" />
                        <IndividualBoardManagement {...sectionProps} />
                        <Divider variant="middle" />
                        <TutorialLibrary {...sectionProps} />
                        <Divider variant="middle" />
                        <MobileManagement {...sectionProps} />
                        <Divider variant="middle" />
                        <GuestAccess {...sectionProps} />
                    </div>

                    {wide ? <TutorialToc sections={sections} variant="aside" /> : null}
                </div>
            </LandingSection>
        </>
    );
};

export default Tutorial;
